import { InjectionToken, Provider } from '@angular/core';

/**
 * Origin that serves the CDN assets — themes, icons, illustrations, fonts,
 * sounds, emoji and docs. Production is `https://cdn.tadone.app`; local
 * development points at libs/cdn's own dev server on :4502.
 *
 * The factory default is the production origin so that specs and any app that
 * has not wired `environment.cdn` behave exactly as they did before this token
 * existed. Never carries a trailing slash — every consumer concatenates a path
 * that starts with `/`.
 */
export const CDN_ORIGIN = new InjectionToken<string>('CDN_ORIGIN', {
  providedIn: 'root',
  factory: () => 'https://cdn.tadone.app',
});

/**
 * Providers for the CDN origin, or none at all when the origin is empty.
 *
 * Returning an empty array rather than `{ useValue: '' }` is the whole point:
 * Angular consults an InjectionToken's factory ONLY when no provider is
 * registered, so an unconditional provider shadows the production default and
 * yields URLs like `/icons/checkmark.svg` resolved against the app's own origin
 * — a 404 that the old proxy-based setup used to mask.
 *
 * The case this actually covers is `cdn: ''`, which is what every
 * `environment.local.ts` held before the local origin existed. Those files are
 * gitignored and copied per-worktree, so stale copies are the norm, not an edge
 * case. A local file missing the `cdn` key entirely is a different matter: it
 * fails the build with TS2339, because `fileReplacements` feeds the replacement
 * to the TypeScript host. That is a fair trade — a compile error beats a page
 * that half-works — but it is why `environment.local.example.ts` is committed.
 */
export const provideCdnOrigin = (origin: string | undefined): Provider[] =>
  origin ? [{ provide: CDN_ORIGIN, useValue: origin }] : [];
